/**
 * The read-only cards of a trip screen, shared by the driver's trip
 * (TripDetail.jsx, Hungarian) and the Operator Admin's trip details
 * (Operator/TripDetails.jsx, English):
 *
 *   Facts         key → value rows (rows without a value are left out)
 *   Note          a labelled note (the dispatcher's message)
 *   ProgrammeCard a programme's stops on the rail — or, when it has none, the
 *                 trip's own ends; `children` go under it (the driver's
 *                 Navigáció / Névtábla buttons)
 *   RouteOrProgramme  whichever of the two this trip is; every screen's
 *                 "where does this go" card
 *   Manifest      the named passenger list
 *   CustomerCard  the customer, and the phone with WhatsApp / call — or,
 *                 while the API withholds it, when it will appear
 *
 * CustomerCard takes two OPT-IN slots, `primary` and `more`, and renders
 * neither unless a screen hands it one: see the comment on the component.
 *
 * Every word comes from `copy` (portalFormat's TRIP_CARD_COPY by default,
 * operatorFormat's TRIP_CARD_COPY_EN on the operator screens), so one card
 * serves both languages. None of them shows a price, a margin or the
 * booking's own comments — they only render what the caller hands them.
 */
import React from 'react';
import { telUrl, whatsappUrl } from '../../../Utils/contactLinks';
import { formatTime } from '../../../Utils/timezone';
import {
  hasProgramme,
  initials,
  passengerManifest,
  phoneReleaseAt,
  place,
  programmeHours,
  programmeName,
  programmeStops,
  TRIP_CARD_COPY,
} from '../portalFormat';
import RouteRail, { endpointStops } from './RouteRail';
import { PhoneIcon, WhatsAppIcon } from './Icons';

/**
 * `rows` are `{ key, value }` — the label and what it says — and a row with
 * no value is left out.
 *
 * `row.id` is optional and is ONLY the React key. Almost every list here has
 * labels that are unique by nature ('Driver', 'Vehicle', 'Flight'), and for
 * those the label is the identity. A list whose labels can repeat — two
 * adjustments booked with the same words on one operator's fee — passes an
 * `id` so the rows stay distinct without putting a disambiguator on screen.
 */
export function Facts({ rows, title = '' }) {
  const present = rows.filter((row) => row.value);
  if (!present.length) return null;
  return (
    <div className="pp-card pp-facts">
      {title ? <div className="pp-eyebrow pp-facts__title">{title}</div> : null}
      {present.map((row, index) => (
        <React.Fragment key={row.id || row.key}>
          {index > 0 ? <div className="pp-facts__sep" /> : null}
          <div className="pp-facts__row">
            <span className="pp-facts__key">{row.key}</span>
            <span className="pp-facts__value">{row.value}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function Note({ label, body }) {
  if (!body) return null;
  return (
    <div className="pp-note">
      <div className="pp-note__label">{label}</div>
      <div className="pp-note__body">{body}</div>
    </div>
  );
}

/** A programme stop (ph-core ProgrammeStop) as a rail stop. */
const programmeRailStop = (copy) => (stop, index) => {
  const spot = place(stop);
  const stay = stop.duration_minutes > 0 ? copy.stay(stop.duration_minutes) : '';
  return {
    // Stops have no id in the payload; their order is the identity.
    key: `${stop.order}-${index}`,
    time: formatTime(stop.arrival_time || stop.departure_time),
    title: spot.title || copy.stop,
    detail: [stop.notes || spot.detail, stay].filter(Boolean).join(' · '),
  };
};

/**
 * A programme, whichever of its two real shapes this trip has: an itinerary on
 * the rail, or just a name and a number of hours.
 *
 * WITH NO STOPS THE RAIL IS THE TRIP'S OWN ENDS — the same pickup → drop-off
 * rail a plain transfer shows. Without an itinerary that IS the whole journey,
 * it is the only route the payload knows, and the driver still needs to read
 * where they start and finish; an empty rail, or no card at all, told them
 * nothing and hid a booked ten-hour day behind a plain A→B route.
 */
export function ProgrammeCard({ trip, copy = TRIP_CARD_COPY, children = null }) {
  const hours = programmeHours(trip);
  const stops = programmeStops(trip).map(programmeRailStop(copy));
  const rail = stops.length ? stops : endpointStops(trip, true, copy.endpoints);
  // 'Programme with stops' is only true when there are some.
  const unnamed = stops.length ? copy.programmeUnnamed : copy.programmeNoStops;

  return (
    <div className="pp-card pp-programme">
      <div className="pp-programme__head">
        <div>
          <div className="pp-eyebrow">{copy.programme}</div>
          <div className="pp-programme__name">
            {programmeName(trip) || unnamed}
          </div>
        </div>
        {hours > 0 ? <span className="pp-programme__hours">{copy.hours(hours)}</span> : null}
      </div>

      <RouteRail stops={rail} />

      {children}
    </div>
  );
}

/**
 * Where this trip goes: its programme when it has one, else plain pickup →
 * drop-off. Every trip screen asks exactly this — the driver's, the operator's
 * and the dispatcher's — and each one used to answer it with its own copy of
 * the branch, so a change to what counts as a programme had to be made three
 * times to be true. `copy` carries the language, `children` the buttons the
 * driver's screen hangs underneath.
 */
export function RouteOrProgramme({ trip, copy = TRIP_CARD_COPY, children = null }) {
  if (hasProgramme(trip)) {
    return <ProgrammeCard trip={trip} copy={copy}>{children}</ProgrammeCard>;
  }

  return (
    <div className="pp-card">
      <RouteRail stops={endpointStops(trip, true, copy.endpoints)} />
      {children ? <div className="pp-gap-top">{children}</div> : null}
    </div>
  );
}

export function Manifest({ trip, copy = TRIP_CARD_COPY }) {
  const people = passengerManifest(trip);
  if (!people.length) return null;

  return (
    <div className="pp-card pp-manifest">
      <div className="pp-manifest__head">
        <div className="pp-eyebrow">
          {trip.event_name ? `${copy.passengers} · ${trip.event_name}` : copy.passengers}
        </div>
        <span className="pp-manifest__count">{copy.people(people.length)}</span>
      </div>
      <ul className="pp-manifest__list">
        {people.map((person, index) => (
          // Two guests can share a name; the list order is the identity.
          // eslint-disable-next-line react/no-array-index-key
          <li className="pp-manifest__row" key={`${person.name}-${index}`}>
            <span className="pp-manifest__avatar" aria-hidden="true">{initials(person.name)}</span>
            <span className="pp-manifest__body">
              <span className="pp-manifest__name">{person.name}</span>
              {person.flight_number ? (
                <span className="pp-manifest__meta">{copy.flight(person.flight_number)}</span>
              ) : null}
              {person.notes ? <span className="pp-manifest__meta">{person.notes}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * `onCall` / `onWhatsApp` — fired when the driver taps either control, so
 * the ATTEMPT is recorded rather than only the intention (the driver portal
 * sends `passenger_contact_attempted`; a no-show later refuses to go
 * without one).
 *
 * BOTH ARE WIRED, because both count. The no-show instruction names the
 * call button, but what it asks for is a recorded attempt to REACH the
 * passenger — and at an airport a WhatsApp, prefilled with the meeting
 * point, the car and the plate, is often what a driver sensibly reaches
 * for. Recording only the call would have told them they had contacted
 * nobody.
 *
 * Deliberately alongside the links rather than instead of them: the hrefs
 * still do the only thing that matters at the kerb, which is open the
 * dialer or the chat. The record is best effort — it rides on the tap and
 * the trip is re-read when the driver comes back to the app. Omitted on the
 * operator and dispatcher screens, where nobody is filing a claim.
 *
 * `waMessage` — what the WhatsApp chat opens with. Empty on the operator
 * and dispatcher screens (and before the driver has set off), which is the
 * old behaviour: an empty chat. The text is the PASSENGER's to read, so it
 * is English wherever it is set — see portalFormat's `passengerMessage`.
 *
 * `primary` / `more` — TWO SLOTS, OFF UNLESS A SCREEN FILLS THEM, because
 * only one of the three screens that render this card may offer what goes
 * in them.
 *
 *   primary  takes the lead action's place — the green `wa.me` link
 *   more     a quiet block under the row, for whatever the lead displaced
 *
 * The DISPATCHER fills both: messaging the passenger as the company leads
 * (Dispatcher/PassengerMessage) and the personal `wa.me` link moves into a
 * sheet behind More options (Dispatcher/PassengerMoreOptions). An OPERATOR
 * and a DRIVER fill neither and so get exactly what they got before —
 * which is the point of the slots rather than a flag. `POST /portal/message`
 * resolves a dispatcher principal and 403s a driver or an operator even on
 * a booking they are assigned to, so a card that offered office messaging
 * to all three would be offering two of them a refusal.
 *
 * `Call` IS NOT IN THE SLOTS. Whatever leads, the one-tap call stays on the
 * card in the same place for all three: it is what anybody reaches for when
 * a passenger is not where they said they would be.
 */
export function CustomerCard({
  trip, copy = TRIP_CARD_COPY, onCall, onWhatsApp, waMessage = '',
  primary = null, more = null,
}) {
  if (!trip.customer_name) return null;
  const phone = trip.customer_phone;

  return (
    <div className="pp-card">
      <div className="pp-person">
        <span className="pp-person__avatar">{initials(trip.customer_name)}</span>
        <span className="pp-person__body">
          <span className="pp-person__name">{trip.customer_name}</span>
          {phone ? <span className="pp-person__phone">{phone}</span> : null}
          {phoneReleaseAt(trip) ? (
            <span className="pp-person__note">{copy.phoneHint(trip)}</span>
          ) : null}
        </span>
      </div>
      {phone ? (
        <>
          {/* `--lead` only when something was handed in: a filled slot's
              label is a sentence rather than one word ("Message as the
              office"), and at phone width it needs the row to wrap instead
              of squeezing it into a 48px box. The default pair is two short
              words and is left exactly as it was. */}
          <div className={`pp-actions pp-gap-top${primary ? ' pp-actions--lead' : ''}`}>
            {primary || (
              <a
                className="pp-btn pp-btn--whatsapp pp-btn--grow pp-btn--contact"
                href={whatsappUrl(phone, waMessage)}
                target="_blank"
                rel="noreferrer"
                onClick={onWhatsApp}
              >
                <WhatsAppIcon size={19} />
                WhatsApp
              </a>
            )}
            <a
              className="pp-btn pp-btn--outline pp-btn--grow pp-btn--contact"
              href={telUrl(phone)}
              onClick={onCall}
            >
              <PhoneIcon size={18} />
              {copy.call}
            </a>
          </div>
          {more ? <div className="pp-person__more">{more}</div> : null}
        </>
      ) : null}
    </div>
  );
}
